import { useEffect, useState } from "react";

import {
    X,
    Crown,
    Phone,
    Check,
    XCircle,
    Clock3,
    UserRound,
    ChevronRight,
    Users,
    Loader2,
} from "lucide-react";

import {
    getJobApplications,
    updateApplicationStatus,
} from "../../../../services/applicationService";

import CompanyViewFreelancerModal from "../../../../Components/CompanyViewFreelancerModal/CompanyViewFreelancerModal";
import Toast from "../../../../Components/Toast/Toast";

import "./ApplicantsModal.scss";

const STATUS_LABEL = {
    pending: {
        label: "Pendente",
        className: "badge--pending",
        icon: Clock3,
    },
    approved: {
        label: "Aprovado",
        className: "badge--approved",
        icon: Check,
    },
    rejected: {
        label: "Recusado",
        className: "badge--rejected",
        icon: XCircle,
    },
};

function getErrorMessage(err, fallback) {
    const data = err?.response?.data;

    if (typeof data === "string") return data;
    if (data?.message) return data.message;
    if (data?.error) return data.error;

    return fallback;
}

export default function ApplicantsModal({ job, onClose }) {
    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(null);
    const [viewingFreelancerId, setViewingFreelancerId] = useState(null);
    const [toast, setToast] = useState({
        message: "",
        type: "success",
    });

    const showToast = (message, type = "success") => {
        setToast({ message, type });
    };

    useEffect(() => {
        const fetchApplicants = async () => {
            try {
                const data = await getJobApplications(job.id);
                setApplicants(data);
            } catch {
                showToast("Erro ao carregar candidatos.", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchApplicants();
    }, [job.id]);

    const handleStatus = async (applicationId, status) => {
        setUpdating(applicationId);

        try {
            await updateApplicationStatus(applicationId, status);

            setApplicants((prev) =>
                prev.map((a) =>
                    a.id === applicationId
                        ? { ...a, status }
                        : a
                )
            );

            showToast(
                status === "approved"
                    ? "Candidato aprovado com sucesso!"
                    : "Candidato recusado.",
                status === "approved" ? "success" : "info"
            );
        } catch (err) {
            const msg = getErrorMessage(
                err,
                "Erro ao atualizar candidatura."
            );

            showToast(msg, "error");
        } finally {
            setUpdating(null);
        }
    };

    return (
        <div className="applicants-overlay" onClick={onClose}>
            <div
                className="applicants-modal"
                onClick={(e) => e.stopPropagation()}
            >
                {/* HEADER */}
                <header className="applicants-modal__header">
                    <div className="applicants-modal__heading">
                        <div className="applicants-modal__heading-icon">
                            <Users size={21} />
                        </div>

                        <div>
                            <div className="applicants-modal__title-row">
                                <h2 className="applicants-modal__title">
                                    Candidatos
                                </h2>

                                {!loading && (
                                    <span className="applicants-modal__count">
                                        {applicants.length}
                                    </span>
                                )}
                            </div>

                            <p className="applicants-modal__subtitle">
                                {job.title}
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="applicants-modal__close"
                        onClick={onClose}
                        aria-label="Fechar candidatos"
                    >
                        <X size={20} />
                    </button>
                </header>

                {/* BODY */}
                <div className="applicants-modal__body">
                    {loading ? (
                        <div className="applicants-modal__state">
                            <div className="applicants-modal__loading-icon">
                                <Loader2 size={25} />
                            </div>

                            <strong>Carregando candidatos</strong>

                            <span>
                                Aguarde enquanto buscamos as candidaturas.
                            </span>
                        </div>
                    ) : applicants.length === 0 ? (
                        <div className="applicants-modal__state">
                            <div className="applicants-modal__empty-icon">
                                <Users size={26} />
                            </div>

                            <strong>Nenhum candidato ainda</strong>

                            <span>
                                Quando alguém se candidatar a esta vaga,
                                aparecerá aqui.
                            </span>
                        </div>
                    ) : (
                        <div className="applicants-list">
                            {applicants.map((a) => {
                                const badge =
                                    STATUS_LABEL[a.status] ??
                                    STATUS_LABEL.pending;

                                const StatusIcon = badge.icon;
                                const isUpdating = updating === a.id;
                                const isGold = Boolean(a.freelancer.isGold);

                                const freelancerName =
                                    a.freelancer.nomeSocial ||
                                    a.freelancer.name ||
                                    "Freelancer";

                                return (
                                    <article
                                        key={a.id}
                                        className={`applicant-card ${
                                            isGold
                                                ? "applicant-card--gold"
                                                : ""
                                        }`}
                                    >
                                        {/* PERFIL */}
                                        <button
                                            type="button"
                                            className="applicant-card__info"
                                            onClick={() =>
                                                setViewingFreelancerId(
                                                    a.freelancer.id
                                                )
                                            }
                                        >
                                            <div className="applicant-card__avatar-wrapper">
                                                {a.freelancer.avatarUrl ? (
                                                    <img
                                                        src={
                                                            a.freelancer
                                                                .avatarUrl
                                                        }
                                                        alt={freelancerName}
                                                        className={`applicant-card__avatar ${
                                                            isGold
                                                                ? "applicant-card__avatar--gold"
                                                                : ""
                                                        }`}
                                                    />
                                                ) : (
                                                    <div
                                                        className={`applicant-card__avatar-fallback ${
                                                            isGold
                                                                ? "applicant-card__avatar--gold"
                                                                : ""
                                                        }`}
                                                    >
                                                        {freelancerName
                                                            .slice(0, 2)
                                                            .toUpperCase()}
                                                    </div>
                                                )}

                                                {isGold && (
                                                    <span
                                                        className="applicant-card__gold-icon"
                                                        title="Freelancer Gold"
                                                    >
                                                        <Crown
                                                            size={11}
                                                            fill="currentColor"
                                                        />
                                                    </span>
                                                )}
                                            </div>

                                            <div className="applicant-card__identity">
                                                <div className="applicant-card__name-row">
                                                    <p className="applicant-card__name">
                                                        {freelancerName}
                                                    </p>

                                                    {isGold && (
                                                        <span className="applicant-card__gold-badge">
                                                            <Crown
                                                                size={11}
                                                                fill="currentColor"
                                                            />
                                                            Gold
                                                        </span>
                                                    )}
                                                </div>

                                                {a.freelancer.phone && (
                                                    <span className="applicant-card__phone">
                                                        <Phone size={13} />
                                                        {a.freelancer.phone}
                                                    </span>
                                                )}

                                                <span className="applicant-card__view-profile">
                                                    Ver perfil
                                                    <ChevronRight size={14} />
                                                </span>
                                            </div>
                                        </button>

                                        {/* AÇÕES */}
                                        <div className="applicant-card__actions">
                                            <span
                                                className={`applicant-card__badge ${badge.className}`}
                                            >
                                                <StatusIcon size={13} />
                                                {badge.label}
                                            </span>

                                            {a.status === "pending" && (
                                                <div className="applicant-card__decision-actions">
                                                    <button
                                                        type="button"
                                                        className="applicant-card__btn applicant-card__btn--approve"
                                                        disabled={isUpdating}
                                                        onClick={() =>
                                                            handleStatus(
                                                                a.id,
                                                                "approved"
                                                            )
                                                        }
                                                    >
                                                        {isUpdating ? (
                                                            <Loader2
                                                                size={15}
                                                                className="applicant-card__spinner"
                                                            />
                                                        ) : (
                                                            <Check size={15} />
                                                        )}

                                                        <span>
                                                            Aprovar
                                                        </span>
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="applicant-card__btn applicant-card__btn--reject"
                                                        disabled={isUpdating}
                                                        onClick={() =>
                                                            handleStatus(
                                                                a.id,
                                                                "rejected"
                                                            )
                                                        }
                                                    >
                                                        {isUpdating ? (
                                                            <Loader2
                                                                size={15}
                                                                className="applicant-card__spinner"
                                                            />
                                                        ) : (
                                                            <XCircle size={15} />
                                                        )}

                                                        <span>
                                                            Recusar
                                                        </span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {viewingFreelancerId && (
                <CompanyViewFreelancerModal
                    freelancerId={viewingFreelancerId}
                    onClose={() => setViewingFreelancerId(null)}
                />
            )}

            <Toast
                message={toast.message}
                type={toast.type}
                onClose={() =>
                    setToast({
                        message: "",
                        type: "success",
                    })
                }
            />
        </div>
    );
}